import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';

import { useInvoice } from '../context/InvoiceContext';
import Select from '../components/Select';
import SearchInput from '../components/SearchInput';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const FILES_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const handleResponse = async (response) => {
  let data;
  try { data = await response.json(); } catch { throw new Error(`Server error (${response.status})`); }
  if (!response.ok) throw new Error(data.message || 'Request failed');
  return data;
};

const interviewService = {
  list: async () => {
    const res = await fetch(`${API_URL}/interviews`, { headers: getAuthHeader() });
    return handleResponse(res);
  },
  create: async (data) => {
    const res = await fetch(`${API_URL}/interviews`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },
  update: async (id, data) => {
    const res = await fetch(`${API_URL}/interviews/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },
  delete: async (id) => {
    const res = await fetch(`${API_URL}/interviews/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(res);
  },
  getUsers: async () => {
    const res = await fetch(`${API_URL}/interviews/users`, { headers: getAuthHeader() });
    return handleResponse(res);
  },
  uploadCv: async (id, file) => {
    const formData = new FormData();
    formData.append('cv', file);
    const res = await fetch(`${API_URL}/interviews/${id}/cv`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: formData
    });
    return handleResponse(res);
  },
  deleteCv: async (id) => {
    const res = await fetch(`${API_URL}/interviews/${id}/cv`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(res);
  },
  uploadDocument: async (id, file, label) => {
    const formData = new FormData();
    formData.append('file', file);
    if (label) formData.append('label', label);
    const res = await fetch(`${API_URL}/interviews/${id}/documents`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: formData
    });
    return handleResponse(res);
  },
  deleteDocument: async (id, docId) => {
    const res = await fetch(`${API_URL}/interviews/${id}/documents/${docId}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(res);
  }
};

const cap = s => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

const ordinal = (n) => { const s = ['th', 'st', 'nd', 'rd']; const v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };
const fmtDate = (val) => {
  if (!val) return '-';
  const d = val instanceof Date ? val : new Date(val.length === 10 ? val + 'T00:00:00' : val);
  if (isNaN(d)) return '-';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${ordinal(d.getDate())} ${months[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`;
};

const STATUS_OPTIONS = [
  { value: 'new',                 label: 'New',                 color: '#6366F1', bg: '#EEF2FF' },
  { value: 'screening',           label: 'Screening',           color: '#0891B2', bg: '#ECFEFF' },
  { value: 'interview_scheduled', label: 'Interview Scheduled', color: '#7C3AED', bg: '#F5F3FF' },
  { value: 'interviewed',         label: 'Interviewed',         color: '#C2410C', bg: '#FFF7ED' },
  { value: 'offered',             label: 'Offered',             color: '#0369A1', bg: '#E0F2FE' },
  { value: 'hired',               label: 'Hired',               color: '#059669', bg: '#ECFDF5' },
  { value: 'rejected',            label: 'Rejected',            color: '#DC2626', bg: '#FEF2F2' },
];

const CLOSED_STATUSES = new Set(['hired', 'rejected']);

const getOpt = (options, value) => options.find(o => o.value === value) || options[0];

const InlineBadgeSelect = ({ value, options, onSave }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target) && triggerRef.current && !triggerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    if (saving) return;
    if (open) { setOpen(false); return; }
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left + rect.width / 2 });
    }
    setOpen(true);
  };

  const handleSelect = async (val, e) => {
    e.stopPropagation();
    const newVal = val === value ? '' : val;
    setSaving(true);
    setOpen(false);
    try { await onSave(newVal); }
    finally { setSaving(false); }
  };

  const opt = getOpt(options, value);
  const hasBadge = opt.value !== '';

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} onClick={e => e.stopPropagation()}>
      <span
        ref={triggerRef}
        onClick={handleOpen}
        style={{
          padding: '2px 8px', fontSize: '11px', fontWeight: 600, borderRadius: '2px',
          letterSpacing: '0.04em', whiteSpace: 'nowrap',
          background: hasBadge ? opt.bg : 'transparent',
          color: hasBadge ? opt.color : '#9CA3AF',
          cursor: saving ? 'default' : 'pointer',
          opacity: saving ? 0.6 : 1,
          userSelect: 'none',
        }}
      >
        {saving ? '…' : opt.label}
      </span>
      {open && pos && ReactDOM.createPortal(
        <div ref={ref} style={{
          position: 'fixed', top: pos.top, left: pos.left,
          transform: 'translateX(-50%)', background: '#fff',
          border: '1px solid #E5E7EB', borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 10000,
          minWidth: '160px', overflow: 'hidden'
        }}>
          {options.map(o => (
            <button key={o.value} onClick={(e) => handleSelect(o.value, e)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '7px 12px', fontSize: '12px',
                fontWeight: o.value === value ? 700 : 400,
                background: o.value === value && o.bg !== 'transparent' ? o.bg : 'transparent',
                color: o.value !== '' ? o.color : '#6B7280',
                border: 'none', cursor: 'pointer'
              }}
              onMouseEnter={e => { if (o.value !== value) e.currentTarget.style.background = '#F9FAFB'; }}
              onMouseLeave={e => { e.currentTarget.style.background = o.value === value && o.bg !== 'transparent' ? o.bg : 'transparent'; }}
            >
              {o.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

const Interviews = () => {
  const { currentUser, showNotification, hasPermission } = useInvoice();

  const isAdmin = ['admin', 'super_admin'].includes(currentUser?.role);
  const canManageInterviews = isAdmin || hasPermission('manage_interviews');

  const [applicants, setApplicants] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [cvUploading, setCvUploading] = useState(false);
  const cvInputRef = useRef(null);
  const [docFile, setDocFile] = useState(null);
  const [docLabel, setDocLabel] = useState('');
  const [docUploading, setDocUploading] = useState(false);
  const docInputRef = useRef(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const defaultAssigneeId = currentUser ? String(currentUser.id) : '';

  const emptyForm = {
    name: '', phone: '', email: '', position: '', location: '',
    status: 'new', notes: '', assigneeId: defaultAssigneeId,
    followUpDate: '', followUpTime: '', followUpDone: false
  };
  const [form, setForm] = useState(emptyForm);

  const loadApplicants = useCallback(async () => {
    try {
      const res = await interviewService.list();
      setApplicants(res.data || []);
    } catch (e) {
      showNotification(e.message || 'Failed to load applicants', 'error');
    }
  }, [showNotification]);

  useEffect(() => { loadApplicants(); }, [loadApplicants]);

  useEffect(() => {
    interviewService.getUsers()
      .then(res => setUsers(res.data || []))
      .catch(() => {});
  }, []);

  const resetForm = () => {
    setForm({ ...emptyForm, assigneeId: defaultAssigneeId });
    setEditingId(null);
    setShowForm(false);
    setCvFile(null);
    setDocFile(null);
    setDocLabel('');
  };

  const handleCvUpload = async () => {
    if (!cvFile || !editingId) return;
    setCvUploading(true);
    try {
      const res = await interviewService.uploadCv(editingId, cvFile);
      setApplicants(prev => prev.map(a => a.id === editingId ? res.data : a));
      setCvFile(null);
      if (cvInputRef.current) cvInputRef.current.value = '';
      showNotification('CV uploaded', 'success');
    } catch (e) {
      showNotification(e.message || 'Failed to upload CV', 'error');
    } finally {
      setCvUploading(false);
    }
  };

  const handleCvDelete = async () => {
    if (!editingId) return;
    setCvUploading(true);
    try {
      await interviewService.deleteCv(editingId);
      setApplicants(prev => prev.map(a => a.id === editingId ? { ...a, cvFilename: null, cvOriginalName: null, cvFilePath: null } : a));
      showNotification('CV removed', 'success');
    } catch (e) {
      showNotification(e.message || 'Failed to remove CV', 'error');
    } finally {
      setCvUploading(false);
    }
  };

  const handleDocUpload = async () => {
    if (!docFile || !editingId) return;
    setDocUploading(true);
    try {
      const res = await interviewService.uploadDocument(editingId, docFile, docLabel);
      setApplicants(prev => prev.map(a => a.id === editingId ? res.data : a));
      setDocFile(null);
      setDocLabel('');
      if (docInputRef.current) docInputRef.current.value = '';
      showNotification('Document uploaded', 'success');
    } catch (e) {
      showNotification(e.message || 'Failed to upload document', 'error');
    } finally {
      setDocUploading(false);
    }
  };

  const handleDocDelete = async (docId) => {
    if (!editingId) return;
    try {
      const res = await interviewService.deleteDocument(editingId, docId);
      setApplicants(prev => prev.map(a => a.id === editingId ? res.data : a));
      showNotification('Document removed', 'success');
    } catch (e) {
      showNotification(e.message || 'Failed to remove document', 'error');
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        assigneeId: form.assigneeId ? Number(form.assigneeId) : null
      };
      if (editingId) {
        await interviewService.update(editingId, payload);
        if (cvFile) await interviewService.uploadCv(editingId, cvFile);
      } else {
        const created = await interviewService.create(payload);
        if (cvFile && created.data?.id) await interviewService.uploadCv(created.data.id, cvFile);
      }
      await loadApplicants();
      resetForm();
    } catch (e) {
      showNotification(e.message || 'Failed to save applicant', 'error');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (a) => {
    setForm({
      name: a.name || '',
      phone: a.phone || '',
      email: a.email || '',
      position: a.position || '',
      location: a.location || '',
      status: a.status || 'new',
      notes: a.notes || '',
      assigneeId: a.assigneeId ? String(a.assigneeId) : '',
      followUpDate: a.followUpDate || '',
      followUpTime: a.followUpTime || '',
      followUpDone: a.followUpDone || false
    });
    setEditingId(a.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try {
      await interviewService.delete(id);
      await loadApplicants();
    } catch (e) {
      showNotification(e.message || 'Failed to delete applicant', 'error');
    }
  };

  const handleFieldSave = async (id, field, value) => {
    try {
      await interviewService.update(id, { [field]: value !== undefined ? value : null });
      await loadApplicants();
    } catch (e) {
      showNotification(e.message || 'Failed to update', 'error');
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().slice(0, 10);

  const isOverdue = (a) => !a.followUpDone && a.followUpDate && a.followUpDate < todayStr;

  const filtered = applicants.filter(a => {
    if (filter === 'closed') {
      if (!CLOSED_STATUSES.has(a.status)) return false;
    } else if (filter === 'all') {
      if (CLOSED_STATUSES.has(a.status)) return false;
    } else {
      if (a.status !== filter) return false;
    }
    if (filterStatus && a.status !== filterStatus) return false;
    if (filterAssignee && String(a.assigneeId || '') !== filterAssignee) return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        a.name?.toLowerCase().includes(s) ||
        a.phone?.toLowerCase().includes(s) ||
        a.email?.toLowerCase().includes(s) ||
        a.position?.toLowerCase().includes(s) ||
        a.location?.toLowerCase().includes(s) ||
        a.notes?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const activeCount = applicants.filter(a => !CLOSED_STATUSES.has(a.status)).length;
  const closedCount = applicants.filter(a => CLOSED_STATUSES.has(a.status)).length;

  const editingApplicant = editingId ? applicants.find(a => a.id === editingId) : null;

  const assigneeOptions = [
    { value: '', label: '— Unassigned —' },
    ...users.map(u => ({ value: String(u.id), label: cap(u.loginId) || `${u.firstName} ${u.lastName}` })),
    ...(form.assigneeId && !users.some(u => String(u.id) === form.assigneeId) && editingApplicant?.assignee
      ? [{ value: String(editingApplicant.assignee.id), label: cap(editingApplicant.assignee.loginId) }]
      : [])
  ];

  return (
    <>
      <div className="main-content">
        <div className="container" style={{ maxWidth: '100%' }}>
          <div className="flex-between mb-lg">
            <h1 style={{ display: 'inline-flex', alignItems: 'baseline', gap: '10px' }}>
              Interviews
              <span style={{
                fontSize: '13px', fontWeight: 600, color: 'var(--color-secondary)',
                background: 'var(--color-gray-100)', padding: '2px 10px',
                borderRadius: '12px', lineHeight: '1.4', position: 'relative', top: '-2px'
              }}>
                {applicants.length}
              </span>
            </h1>
            {!showForm && canManageInterviews && (
              <button onClick={() => { resetForm(); setShowForm(true); }} className="btn btn-primary">
                + Add Applicant
              </button>
            )}
          </div>

          {/* Status Filter Tabs */}
          {!showForm && (
            <div style={{ display: 'flex', gap: '4px', marginBottom: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                onClick={() => { setFilter('all'); setFilterStatus(''); }}
                className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
                style={{ fontSize: '11px', padding: '2px 8px' }}
              >
                All ({activeCount})
              </button>
              {STATUS_OPTIONS.filter(s => !CLOSED_STATUSES.has(s.value)).map(s => {
                const count = applicants.filter(a => a.status === s.value).length;
                if (count === 0) return null;
                return (
                  <button
                    key={s.value}
                    onClick={() => { setFilter(s.value); setFilterStatus(''); }}
                    className={`btn btn-sm ${filter === s.value ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ fontSize: '11px', padding: '2px 8px' }}
                  >
                    {s.label} ({count})
                  </button>
                );
              })}
              <span style={{ width: '1px', height: '16px', background: 'var(--color-border)', margin: '0 2px', display: 'inline-block' }} />
              <button
                onClick={() => { setFilter('closed'); setFilterStatus(''); }}
                className={`btn btn-sm ${filter === 'closed' ? 'btn-primary' : 'btn-secondary'}`}
                style={{ fontSize: '11px', padding: '2px 8px' }}
              >
                Closed ({closedCount})
              </button>
            </div>
          )}

          {/* Search & Filters */}
          {!showForm && (
            <div style={{ display: 'flex', gap: '5px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
              <SearchInput
                placeholder="Search..."
                value={search}
                onChange={setSearch}
                style={{ width: '160px', flexShrink: 0 }}
                inputStyle={{ fontSize: '12px', padding: '4px 8px' }}
              />
              <Select
                value={filterAssignee}
                onChange={setFilterAssignee}
                options={[
                  { value: '', label: 'Assignee' },
                  ...users.map(u => ({ value: String(u.id), label: cap(u.loginId) || `${u.firstName} ${u.lastName}` }))
                ]}
                style={{ width: 'auto', flexShrink: 0 }}
                buttonStyle={{
                  fontSize: '12px', padding: '4px 8px',
                  background: filterAssignee ? '#EEF2FF' : undefined,
                  color: filterAssignee ? '#4338CA' : undefined
                }}
              />
              <Select
                value={filterStatus}
                onChange={setFilterStatus}
                options={[
                  { value: '', label: 'Status' },
                  ...(filter === 'closed'
                    ? STATUS_OPTIONS.filter(s => CLOSED_STATUSES.has(s.value))
                    : STATUS_OPTIONS.filter(s => !CLOSED_STATUSES.has(s.value))
                  ).map(s => ({ value: s.value, label: s.label }))
                ]}
                style={{ width: 'auto', flexShrink: 0 }}
                buttonStyle={{
                  fontSize: '12px', padding: '4px 8px',
                  background: filterStatus ? '#EEF2FF' : undefined,
                  color: filterStatus ? '#4338CA' : undefined
                }}
              />
              {(search || filterAssignee || filterStatus) && (
                <button
                  onClick={() => { setSearch(''); setFilterAssignee(''); setFilterStatus(''); }}
                  style={{
                    fontSize: '11px', padding: '2px 7px', height: '26px',
                    background: 'none', border: '1px solid var(--color-border)',
                    borderRadius: '6px', cursor: 'pointer',
                    color: 'var(--color-secondary)', whiteSpace: 'nowrap', flexShrink: 0
                  }}
                >
                  ✕
                </button>
              )}
              <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginLeft: '4px', whiteSpace: 'nowrap' }}>
                {filtered.length} {filtered.length === 1 ? 'applicant' : 'applicants'}
              </span>
            </div>
          )}

          {/* Create/Edit Form */}
          {showForm && (
            <div className="card mb-md" style={{ padding: 'var(--spacing-md)' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: 'var(--spacing-sm)' }}>
                {editingId ? 'Edit Applicant' : 'New Applicant'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Name *</label>
                  <input
                    className="form-input"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    style={{ fontSize: '12px', padding: '4px 8px', borderColor: !form.name.trim() ? '#DC2626' : undefined }}
                    placeholder="Applicant name"
                  />
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Phone</label>
                  <input
                    className="form-input"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    style={{ fontSize: '12px', padding: '4px 8px' }}
                    placeholder="+91 98765 43210"
                  />
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Email</label>
                  <input
                    className="form-input"
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    style={{ fontSize: '12px', padding: '4px 8px' }}
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Position</label>
                  <input
                    className="form-input"
                    value={form.position}
                    onChange={e => setForm(f => ({ ...f, position: e.target.value }))}
                    style={{ fontSize: '12px', padding: '4px 8px' }}
                    placeholder="Job role / position applied for"
                  />
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Location</label>
                  <input
                    className="form-input"
                    value={form.location}
                    onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                    style={{ fontSize: '12px', padding: '4px 8px' }}
                    placeholder="e.g. Madurai, Trichy"
                  />
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status</label>
                  <Select
                    value={form.status}
                    onChange={v => setForm(f => ({ ...f, status: v }))}
                    options={STATUS_OPTIONS}
                    buttonStyle={{ fontSize: '12px', padding: '4px 8px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Assigned To</label>
                  <Select
                    value={form.assigneeId}
                    onChange={v => setForm(f => ({ ...f, assigneeId: v }))}
                    options={assigneeOptions}
                    buttonStyle={{ fontSize: '12px', padding: '4px 8px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Follow-up Date &amp; Time</label>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <input
                      className="form-input"
                      type="date"
                      value={form.followUpDate}
                      onChange={e => setForm(f => ({ ...f, followUpDate: e.target.value, followUpDone: false }))}
                      style={{ fontSize: '12px', padding: '4px 8px', flex: 1 }}
                    />
                    <input
                      className="form-input"
                      type="time"
                      value={form.followUpTime}
                      onChange={e => setForm(f => ({ ...f, followUpTime: e.target.value }))}
                      style={{ fontSize: '12px', padding: '4px 8px', width: '90px' }}
                    />
                  </div>
                </div>
              </div>
              <div style={{ marginTop: '8px' }}>
                <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Notes</label>
                <textarea
                  className="form-input"
                  style={{ minHeight: '40px', resize: 'none', whiteSpace: 'pre-wrap', overflow: 'hidden', fontSize: '12px', padding: '4px 8px' }}
                  value={form.notes}
                  onChange={e => {
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                    setForm(f => ({ ...f, notes: e.target.value }));
                  }}
                  ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                  placeholder="Additional details..."
                />
              </div>
              {(() => {
                const current = editingId ? applicants.find(a => a.id === editingId) : null;
                return (
                  <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--color-border)' }}>
                    <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '6px' }}>CV / Resume</label>
                    {current?.cvFilename ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <a
                          href={`${FILES_URL}/uploads/${current.cvFilename}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: '12px', color: '#0369A1', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          📄 {current.cvOriginalName}
                        </a>
                        <button
                          className="btn btn-sm"
                          style={{ fontSize: '11px', padding: '2px 8px', background: 'none', border: '1px solid #FCA5A5', color: '#DC2626', borderRadius: '4px', cursor: 'pointer' }}
                          onClick={handleCvDelete}
                          disabled={cvUploading}
                        >
                          Remove
                        </button>
                        <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>or replace:</span>
                        <input ref={cvInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={e => setCvFile(e.target.files[0] || null)} style={{ fontSize: '12px' }} />
                        {cvFile && (
                          <button className="btn btn-primary btn-sm" style={{ fontSize: '11px', padding: '2px 8px' }} onClick={handleCvUpload} disabled={cvUploading}>
                            {cvUploading ? 'Uploading…' : 'Upload'}
                          </button>
                        )}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input ref={cvInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={e => setCvFile(e.target.files[0] || null)} style={{ fontSize: '12px' }} />
                        {cvFile && editingId && (
                          <button className="btn btn-primary btn-sm" style={{ fontSize: '11px', padding: '2px 8px' }} onClick={handleCvUpload} disabled={cvUploading}>
                            {cvUploading ? 'Uploading…' : 'Upload'}
                          </button>
                        )}
                        {cvFile && !editingId && (
                          <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>{cvFile.name} — will upload on save</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
              {editingId && (() => {
                const current = applicants.find(a => a.id === editingId);
                const docs = current?.documents || [];
                return (
                  <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--color-border)' }}>
                    <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '6px' }}>
                      Supporting Documents {docs.length > 0 && `(${docs.length})`}
                    </label>
                    {docs.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>
                        {docs.map(doc => (
                          <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px', background: 'var(--color-gray-50, #F9FAFB)', borderRadius: '4px' }}>
                            <a
                              href={`${FILES_URL}/uploads/${doc.filename}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ fontSize: '12px', color: '#0369A1', textDecoration: 'none', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              📎 {doc.label || doc.originalName}
                            </a>
                            <button
                              className="btn btn-sm"
                              style={{ fontSize: '11px', padding: '2px 8px', background: 'none', border: '1px solid #FCA5A5', color: '#DC2626', borderRadius: '4px', cursor: 'pointer', flexShrink: 0 }}
                              onClick={() => handleDocDelete(doc.id)}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <input
                        className="form-input"
                        style={{ fontSize: '12px', padding: '4px 8px', width: '140px' }}
                        placeholder="Label (optional)"
                        value={docLabel}
                        onChange={e => setDocLabel(e.target.value)}
                      />
                      <input ref={docInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={e => setDocFile(e.target.files[0] || null)} style={{ fontSize: '12px' }} />
                      {docFile && (
                        <button className="btn btn-primary btn-sm" style={{ fontSize: '11px', padding: '2px 8px' }} onClick={handleDocUpload} disabled={docUploading}>
                          {docUploading ? 'Uploading…' : 'Upload'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving || !form.name.trim()}
                >
                  {saving ? 'Saving…' : (editingId ? 'Save Changes' : 'Add Applicant')}
                </button>
                <button className="btn btn-secondary" onClick={resetForm}>Cancel</button>
              </div>
            </div>
          )}

          {/* Table */}
          {!showForm && (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-gray-50)' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Name</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Phone</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Email</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Position / Location</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Follow-up</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Assigned To</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ padding: '24px', textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: '13px' }}>
                        No applicants found.
                      </td>
                    </tr>
                  )}
                  {filtered.map((a, idx) => {
                    const overdue = isOverdue(a);
                    const assigneeUsers = [
                      { value: '', label: '— Unassigned —' },
                      ...users.map(u => ({ value: String(u.id), label: cap(u.loginId) || `${u.firstName} ${u.lastName}` })),
                      ...(a.assigneeId && !users.some(u => String(u.id) === String(a.assigneeId)) && a.assignee
                        ? [{ value: String(a.assignee.id), label: cap(a.assignee.loginId) }]
                        : [])
                    ];
                    return (
                      <tr
                        key={a.id}
                        style={{
                          borderBottom: idx < filtered.length - 1 ? '1px solid var(--color-border)' : 'none',
                          background: 'white'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--color-gray-50)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'white'}
                      >
                        <td style={{ padding: '8px 12px', fontWeight: 600, color: 'var(--color-text)', whiteSpace: 'nowrap' }}>
                          {a.name}
                        </td>
                        <td style={{ padding: '8px 12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                          {a.phone || '—'}
                        </td>
                        <td style={{ padding: '8px 12px', color: 'var(--color-text-secondary)', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.email || '—'}
                        </td>
                        <td style={{ padding: '8px 12px', color: 'var(--color-text-secondary)' }}>
                          {a.position || '—'}
                          {a.location && <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', opacity: 0.75 }}>📍 {a.location}</div>}
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          {a.followUpDate ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span
                                style={{
                                  fontSize: '12px',
                                  color: overdue ? '#DC2626' : 'var(--color-text-secondary)',
                                  fontWeight: overdue ? 600 : 400
                                }}
                              >
                                {fmtDate(a.followUpDate)}
                                {a.followUpTime && (() => {
                                  const [h, m] = a.followUpTime.split(':');
                                  const hr = +h;
                                  return ` ${hr % 12 || 12}:${m} ${hr < 12 ? 'AM' : 'PM'}`;
                                })()}
                              </span>
                              {a.followUpDone && (
                                <span
                                  title="Follow-up done"
                                  onClick={e => { e.stopPropagation(); handleFieldSave(a.id, 'followUpDone', false); }}
                                  style={{ color: '#059669', cursor: 'pointer', fontSize: '13px', fontWeight: 700 }}
                                >
                                  ✓
                                </span>
                              )}
                              {!a.followUpDone && (
                                <span
                                  title="Mark follow-up done"
                                  onClick={e => { e.stopPropagation(); handleFieldSave(a.id, 'followUpDone', true); }}
                                  style={{ color: '#9CA3AF', cursor: 'pointer', fontSize: '11px' }}
                                >
                                  ○
                                </span>
                              )}
                            </div>
                          ) : '—'}
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <InlineBadgeSelect
                            value={a.assigneeId ? String(a.assigneeId) : ''}
                            options={assigneeUsers.map(u => ({
                              value: u.value,
                              label: u.label,
                              color: '#374151',
                              bg: u.value ? '#F3F4F6' : 'transparent'
                            }))}
                            onSave={val => handleFieldSave(a.id, 'assigneeId', val ? Number(val) : null)}
                          />
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <InlineBadgeSelect
                            value={a.status}
                            options={STATUS_OPTIONS}
                            onSave={val => handleFieldSave(a.id, 'status', val || 'new')}
                          />
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            {a.cvFilename && (
                              <a
                                href={`${FILES_URL}/uploads/${a.cvFilename}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={a.cvOriginalName}
                                style={{ fontSize: '11px', padding: '2px 7px', background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', borderRadius: '4px', textDecoration: 'none', whiteSpace: 'nowrap' }}
                              >
                                📄 CV
                              </a>
                            )}
                            <button
                              className="btn btn-secondary btn-sm"
                              style={{ fontSize: '11px', padding: '2px 8px' }}
                              onClick={() => startEdit(a)}
                            >
                              Edit
                            </button>
                            {canManageInterviews && (
                              <button
                                className="btn btn-sm"
                                style={{ fontSize: '11px', padding: '2px 8px', background: 'none', border: '1px solid #FCA5A5', color: '#DC2626', borderRadius: '4px', cursor: 'pointer' }}
                                onClick={() => handleDelete(a.id)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Interviews;
